import { useEffect, useRef, useState } from 'react';
import { experienceTemplates as templates } from '../experienceTemplatesDummyData';
import { processingFlowTemplates as processingTemplates } from '../processingFlowTemplateDummyData';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import { useLocation, useNavigate } from 'react-router-dom';
import { processExperienceTemplate } from '@/src/lib/templateUtils';
import { processFlowTemplate } from '@/src/lib/templateUtils';
const initialSteps = [
    {
        id: 'Step 1',
        name: 'Event Details',
        status: 'current',
    },
    {
        id: 'Step 2',
        name: 'Experience & Creative',
        status: 'upcoming',
    },
];

export interface DefinitionTemplate {
    name: string;
    description: string;
    demo?: string;
    definitionJsonString: string;
}

export default function useCreateFirstEvent() {
    const location = useLocation();
    const navigate = useNavigate();
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();

    console.log('orgId', orgId);

    const isCreatingEvent = useRef(false);

    const [steps, setSteps] = useState(initialSteps);
    const [platform, setPlatform] = useState<null | 'romr' | 'clickerbooth'>(
        null,
    );
    const [experience, setExperience] = useState<DefinitionTemplate | null>(
        null,
    );

    const [experienceTemplates, setExperienceTemplates] = useState<
        DefinitionTemplate[]
    >([]);

    const [processingFlowTemplates, setProcessingFlowTemplates] = useState<
        DefinitionTemplate[]
    >([]);

    const [shareViaText, setShareViaText] = useState(true);
    const [shareViaEmail, setShareViaEmail] = useState(true);
    const [customMessage, setCustomMessage] = useState('');
    const [eventName, setEventName] = useState('');
    const [eventDate, setEventDate] = useState<Date>(new Date());
    const [eventLocation, setEventLocation] = useState<string | null>(null);
    const [eventTemplate, setEventTemplate] =
        useState<DefinitionTemplate | null>(null);
    const [processingFlow, setProcessingFlow] =
        useState<DefinitionTemplate | null>(null);
    const [processingFlowTemplate, setProcessingFlowTemplate] =
        useState<DefinitionTemplate | null>(null);
    const [galleryLogo, setGalleryLogo] = useState<string | null>(null);
    const [foregroundColor, setForegroundColor] = useState<string>('#000000');
    const [backgroundColor, setBackgroundColor] = useState<string>('#FFFFFF');
    const [headerForegroundColor, setHeaderForegroundColor] =
        useState<string>('#FFFFFF');
    const [headerBackgroundColor, setHeaderBackgroundColor] =
        useState<string>('#000000');

    const [templateValues, setTemplateValues] = useState<Record<string, any>>(
        {},
    );

    useEffect(() => {
        const fetchExperienceTemplates = async () => {
            setExperienceTemplates(templates);
        };
        fetchExperienceTemplates();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchProcessingFlowTemplates = async () => {
            setProcessingFlowTemplates(processingTemplates);
        };
        fetchProcessingFlowTemplates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (
            experienceTemplates.length === 0 &&
            processingFlowTemplates.length === 0
        ) {
            return;
        }

        if (platform === 'romr') {
            setProcessingFlowTemplate(processingFlowTemplates[0]);
        } else if (platform === 'clickerbooth') {
            setExperience(experienceTemplates[0]);
        }
    }, [platform, processingFlowTemplates, experienceTemplates]);

    useEffect(() => {
        if (experience === null && processingFlowTemplate === null) {
            return;
        }
        if (platform === 'romr') {
            const definition = processFlowTemplate(
                templateValues,
                processingFlowTemplate as unknown as DefinitionTemplate,
            );
            recordValues({
                processingFlow: definition,
            });
        } else if (platform === 'clickerbooth') {
            const definition = processExperienceTemplate(
                templateValues,
                experience as unknown as DefinitionTemplate,
            );
            recordValues({
                eventTemplate: definition,
            });
        }
    }, [templateValues, platform, experience, processingFlowTemplate]);

    const recordValues = (
        values: Record<
            string,
            string | Date | boolean | DefinitionTemplate | null | number
        >,
    ) => {
        console.log('Recording values', values);
        for (const [key, value] of Object.entries(values)) {
            if (key === 'galleryLogo') {
                setGalleryLogo(value as string);
            } else if (key === 'eventName') {
                setEventName(value as string);
            } else if (key === 'eventDate') {
                setEventDate(new Date(value as string));
            } else if (key === 'eventLocation') {
                setEventLocation(value as string);
            } else if (key === 'shareViaText') {
                setShareViaText(value as boolean);
            } else if (key === 'shareViaEmail') {
                setShareViaEmail(value as boolean);
            } else if (key === 'customMessage') {
                setCustomMessage(value as string);
            } else if (key === 'eventTemplate') {
                setEventTemplate(value as unknown as DefinitionTemplate);
            } else if (key === 'experience') {
                setExperience(value as DefinitionTemplate);
            } else if (key === 'processingFlow') {
                setProcessingFlow(value as DefinitionTemplate);
            } else if (key === 'processingFlowTemplate') {
                setProcessingFlowTemplate(value as DefinitionTemplate);
            } else if (key === 'foregroundColor') {
                setForegroundColor(value as string);
            } else if (key === 'backgroundColor') {
                setBackgroundColor(value as string);
            } else if (key === 'headerForegroundColor') {
                setHeaderForegroundColor(value as string);
            } else if (key === 'headerBackgroundColor') {
                setHeaderBackgroundColor(value as string);
            }
        }
    };

    const goToStep = (stepId: string) => {
        const newStepIndex = steps.findIndex((step) => step.id === stepId);
        setSteps((prevSteps) =>
            prevSteps.map((step, index) =>
                index === newStepIndex
                    ? { ...step, status: 'current' }
                    : {
                          ...step,
                          status:
                              index < newStepIndex ? 'complete' : 'upcoming',
                      },
            ),
        );
    };

    const saveEvent = async () => {
        if (isCreatingEvent.current) {
            return;
        }
        isCreatingEvent.current = true;
        const experienceDefinition = eventTemplate;
        const processingFlowDefinition = processingFlow;

        const requestBody = {
            details: {
                name: eventName,
                date: eventDate,
                location: eventLocation,
            },
            experience: {
                experience: experienceDefinition,
                processingFlow: processingFlowDefinition,
            },
            sharing: {
                shareViaText: shareViaText,
                shareViaEmail: shareViaEmail,
                customMessage: customMessage,
            },
            gallerySettings: {
                logo: galleryLogo,
                name: eventName,
                foregroundColor: foregroundColor,
                backgroundColor: backgroundColor,
                headerForegroundColor: headerForegroundColor,
                headerBackgroundColor: headerBackgroundColor,
            },
        };

        console.log('requestBody', requestBody);

        let from = location.state?.from?.pathname || '/';

        try {
            const response = await authenticatedApi.post(
                `/${orgId}/activations/v2/create`,
                requestBody,
            );

            console.log('from', from);
            console.log('response', response);
            let data = response?.data;
            if (from === '/' && data) {
                navigate(`/events/${data.id}`);
            } else {
                navigate(from);
            }
            console.log('saveEvent', response);
        } catch (error) {
            console.error('Error saving event', error);
        } finally {
            isCreatingEvent.current = false;
        }
    };

    return {
        steps,
        goToStep,
        platform,
        setPlatform,
        experience,
        setExperience,
        experienceTemplates,
        recordValues,
        saveEvent,
        processingFlow,
        setProcessingFlow,
        processingFlowTemplates,
        foregroundColor,
        setForegroundColor,
        backgroundColor,
        setBackgroundColor,
        headerForegroundColor,
        setHeaderForegroundColor,
        headerBackgroundColor,
        setHeaderBackgroundColor,
        customMessage,
        setCustomMessage,
        shareViaText,
        setShareViaText,
        shareViaEmail,
        setShareViaEmail,
        galleryLogo,
        setGalleryLogo,
        templateValues,
        setTemplateValues,
        processingFlowTemplate,
        setProcessingFlowTemplate,
        eventName,
        setEventName,
        eventDate,
        setEventDate,
        eventLocation,
        setEventLocation,
    };
}
